import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Readout — Complao's own replies, read aloud. M31.
 *
 * **The browser speaks, not Complao.** This is `speechSynthesis`, the same Web Speech API
 * M30 dictates through, chosen for the same reason: no route, no clip, no per-minute cost.
 * It is the quieter half of that API — synthesis is normally performed by voices installed
 * on the client's own machine, so unlike recognition it usually sends nothing anywhere. ⚠
 * **On Chrome it now sends, by choice.** `PREFERRED_VOICE` names one of Chrome's network
 * voices, so where that voice exists the text to be spoken goes to its vendor on every
 * press — this is a decision (1 September 2026), not the incidental risk the paragraph
 * that stood here described. What is sent is a question Complao composed and already
 * displayed, never a client's answer and never a document. Do not describe the readout in
 * the interface as private, for the same reason M30's mic is not described that way, and
 * do not remove the `network` refusal below: a network voice is the one that can be
 * unreachable.
 *
 * **Nothing is recorded and nothing is heard.** There is no audio element, no capture and
 * no upload. This reads text that is already on the screen; the record of the session is
 * untouched, no row is written, and whether a message was played is not a fact the platform
 * keeps.
 *
 * **It is pressed, never automatic.** Decided 31 August 2026, against an off-by-default
 * auto-play toggle that was offered. A reply that begins speaking by itself speaks into
 * whatever room the console is open in — a client's meeting room with their own staff in
 * it, or a lead auditor's office — and neither viewer has consented to that by opening an
 * audit. Do not add auto-play later without recording the decision: the control is safe
 * precisely because nothing sounds until somebody asks for it.
 *
 * **One thing speaks at a time**, because `speechSynthesis` is one global queue per
 * document. Pressing a second message stops the first rather than layering, and the state
 * here holds *which* message is speaking so exactly one button can show it.
 */

/**
 * The audit is conducted in English and the voice is told so rather than left to read the
 * browser's locale — the same fixed statement `dictation.ts` makes for the same reason. A
 * question about ISO 9001 Cl. 4.1 read by a French voice against English text is not
 * comprehensible to the client it was written for.
 */
const AUDIT_LANGUAGE = "en-GB";

/**
 * The voice, named rather than left to the device. Decided 1 September 2026 after listening
 * to the installed set: `pickVoice` took the *first* `en-GB` entry `getVoices()` returned,
 * which differs per machine, so a client and their lead auditor heard the same audit read
 * in two different voices and neither was chosen.
 *
 * ⚠ It is matched by name and it is **first, not only**. This voice ships with Chrome and
 * exists nowhere else, so Safari, Firefox and Edge fall straight through to the language
 * chain below — a pinned voice that was the sole test would mean silence on three browsers
 * the console runs in.
 */
const PREFERRED_VOICE = "Google US English";

/**
 * ⚠ **A long utterance is cut off, and that is a browser defect, not a limit.** Chrome
 * stops speaking after roughly fifteen seconds of a single utterance — silently, mid-word,
 * with no error — so a message read as one utterance ends part-way through a question and
 * the listener has no way to know a clause was dropped. The message is therefore spoken as
 * a queue of sentence-sized utterances, split at sentence ends first and at word
 * boundaries only where a single sentence is longer than the cap — so the pauses fall
 * where the writing already pauses.
 *
 * ⚠ **410 is above the arithmetic the original 180 was drawn from**, and it was set
 * deliberately on 1 September 2026 after listening to the message through, because the
 * shorter cap broke a clause reference across two utterances often enough to be heard.
 * At `SPEAKING_RATE` a full 410-character segment is roughly twenty-three seconds of
 * English, so a segment that packs full **can** reach the cutoff. The failure is silent
 * and mid-word: nothing errors and `onend` still arrives, so neither the watchdog below
 * nor the refusals catch it. If a message is ever reported as stopping part-way, this
 * number is the first thing to look at — lower it, or keep a `resume()` running.
 */
const SEGMENT_CHARS = 410;

/**
 * Slightly quicker than the browser's default of 1. Decided 1 September 2026 against the
 * default and against anything faster: an audit question is heard once, by a client who
 * cannot ask the speaker to slow down, and a clause reference read at speed is the part
 * they most need to catch.
 *
 * ⚠ **Rate and `SEGMENT_CHARS` are one setting in two halves.** The cutoff below is fifteen
 * seconds of wall clock, not a character count, so raising this shortens how much text fits
 * inside it. Neither may be changed without the other being reconsidered.
 */
const SPEAKING_RATE = 1.15;

/**
 * `cancel()` followed immediately by `speak()` leaves Chrome silent — the new utterance is
 * dropped along with the ones being cancelled. Pressing a second message while a first is
 * speaking therefore stops the first and starts the second a beat later.
 */
const RESTART_MS = 120;

/**
 * A browser with the API but no installed voice speaks nothing and reports nothing — the
 * one failure this control could have that is indistinguishable from working. If the first
 * utterance has not started by then, it is not going to.
 */
const SILENT_START_MS = 2500;

const REFUSALS: Record<string, string> = {
  "not-allowed":
    "Your browser would not play audio. Allow sound for this site, then press the speaker again.",
  "audio-busy":
    "The audio device is in use by something else. Close whatever is using it, then press the speaker again.",
  "audio-hardware":
    "No audio output was found. Check your speakers or headphones are connected, then press the speaker again.",
  network:
    "The voice this browser uses is fetched over the network and could not be reached. Read the message above instead, or try again.",
  "synthesis-unavailable":
    "This browser cannot read text aloud. Read the message above instead.",
  "synthesis-failed":
    "The browser could not read this message aloud. Read it above instead, or try the speaker again.",
  "language-unavailable":
    "No English voice is installed on this device, so the message cannot be read aloud. Read it above instead.",
  "voice-unavailable":
    "The voice this browser chose is no longer available. Read the message above instead, or try the speaker again.",
  "text-too-long":
    "The browser refused to read a message this long. Read it above instead.",
};

const SILENT = "Nothing was heard — this device may have no speech voice installed. Read the message above instead.";

export type Readout = {
  /** False only where the API is absent altogether. The button is not drawn at all then. */
  supported: boolean;
  /** The id of the message being spoken, or null. One at a time — it is one global queue. */
  speaking: string | null;
  /**
   * Why a readout did not happen, and which message it was pressed on — shown under that
   * message rather than in a banner. A speaker that quietly does nothing is the failure
   * this control most easily has, and it is the one a listener cannot detect.
   */
  problem: { id: string; message: string } | null;
  /** Press a message's speaker: speak it, or stop it if it is the one already speaking. */
  toggle: (id: string, text: string) => void;
  stop: () => void;
};

/**
 * `PREFERRED_VOICE` where it exists, `en-GB` if not, any other English after that, and the
 * browser's own last. Every step after the first is the chain that stood before the voice
 * was pinned, kept intact so a browser without it still speaks.
 */
function pickVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  return (
    voices.find((voice) => voice.name === PREFERRED_VOICE) ??
    voices.find((voice) => voice.lang.replace("_", "-") === AUDIT_LANGUAGE) ??
    voices.find((voice) => voice.lang.replace("_", "-").startsWith("en-")) ??
    null
  );
}

/**
 * Sentence ends, found by scanning rather than by a lookbehind — a regex literal using one
 * is a *parse* error in Safari before 16.4, which would take the whole bundle down on a
 * browser the console otherwise runs in.
 */
function sentences(text: string): string[] {
  const out: string[] = [];
  let start = 0;
  for (let index = 0; index < text.length; index += 1) {
    const here = text[index] ?? "";
    const next = text[index + 1];
    const ends = here === "." || here === "!" || here === "?" || here === ":" || here === "\n";
    if (ends && (next === undefined || /\s/.test(next))) {
      out.push(text.slice(start, index + 1));
      start = index + 1;
    }
  }
  if (start < text.length) out.push(text.slice(start));
  return out.map((part) => part.trim()).filter(Boolean);
}

/** Sentence-sized utterances, none longer than the cap. */
export function segments(text: string): string[] {
  const out: string[] = [];
  let current = "";

  const flush = () => {
    if (current.trim()) out.push(current.trim());
    current = "";
  };

  const add = (piece: string) => {
    if (!piece) return;
    if (current && `${current} ${piece}`.length > SEGMENT_CHARS) flush();
    current = current ? `${current} ${piece}` : piece;
  };

  for (const sentence of sentences(text)) {
    if (sentence.length <= SEGMENT_CHARS) {
      add(sentence);
      continue;
    }
    // One sentence longer than a whole utterance — broken at spaces, which is where the
    // listener will least notice the seam.
    let run = "";
    for (const word of sentence.split(/\s+/)) {
      if (run && `${run} ${word}`.length > SEGMENT_CHARS) {
        add(run);
        flush();
        run = "";
      }
      run = run ? `${run} ${word}` : word;
    }
    add(run);
  }
  flush();
  return out;
}

export function useReadout(): Readout {
  const [supported] = useState(
    () =>
      typeof window !== "undefined" &&
      "speechSynthesis" in window &&
      "SpeechSynthesisUtterance" in window,
  );
  const [speaking, setSpeaking] = useState<string | null>(null);
  const [problem, setProblem] = useState<{ id: string; message: string } | null>(null);

  // Which readout the callbacks belong to. `speechSynthesis` delivers `onend` and `onerror`
  // for utterances that have already been cancelled, so a stale one would clear the state of
  // the readout that replaced it — the second message would go silent in the interface while
  // still speaking out loud.
  const generation = useRef(0);
  const timers = useRef<number[]>([]);
  const voices = useRef<SpeechSynthesisVoice[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current = [];
  }, []);

  // The list arrives asynchronously in Chrome — `getVoices()` is empty on the first render
  // and fills later — so it is read now and again whenever the browser says it changed.
  useEffect(() => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    const read = () => {
      voices.current = synth.getVoices();
    };
    read();
    synth.addEventListener("voiceschanged", read);
    return () => synth.removeEventListener("voiceschanged", read);
  }, [supported]);

  const stop = useCallback(() => {
    generation.current += 1;
    clearTimers();
    setSpeaking(null);
    if (supported) window.speechSynthesis.cancel();
  }, [supported, clearTimers]);

  const speak = useCallback(
    (id: string, text: string) => {
      if (!supported) return;
      const parts = segments(text);
      if (parts.length === 0) return;

      const synth = window.speechSynthesis;
      const wasSpeaking = synth.speaking || synth.pending;
      generation.current += 1;
      const mine = generation.current;
      clearTimers();
      synth.cancel();
      setProblem(null);
      setSpeaking(id);

      const fail = (message: string) => {
        if (generation.current !== mine) return;
        generation.current += 1;
        clearTimers();
        synth.cancel();
        setSpeaking(null);
        setProblem({ id, message });
      };

      const begin = () => {
        if (generation.current !== mine) return;
        const voice = pickVoice(voices.current);
        parts.forEach((part, index) => {
          const utterance = new SpeechSynthesisUtterance(part);
          utterance.lang = AUDIT_LANGUAGE;
          utterance.rate = SPEAKING_RATE;
          if (voice) utterance.voice = voice;
          if (index === 0) {
            utterance.onstart = () => clearTimers();
          }
          utterance.onerror = (event) => {
            // We cancelled it: the generation has already moved, and both of these arrive
            // as a matter of course when a listener presses stop or plays another message.
            if (event.error === "canceled" || event.error === "interrupted") return;
            fail(
              REFUSALS[event.error] ??
                "The browser stopped reading this message aloud. Read it above instead, or try the speaker again.",
            );
          };
          if (index === parts.length - 1) {
            utterance.onend = () => {
              if (generation.current === mine) setSpeaking(null);
            };
          }
          synth.speak(utterance);
        });
        // Nothing started, nothing errored — the silent failure. Cancelled by `onstart`.
        timers.current.push(window.setTimeout(() => fail(SILENT), SILENT_START_MS));
      };

      if (wasSpeaking) timers.current.push(window.setTimeout(begin, RESTART_MS));
      else begin();
    },
    [supported, clearTimers],
  );

  const toggle = useCallback(
    (id: string, text: string) => {
      if (speaking === id) stop();
      else speak(id, text);
    },
    [speaking, speak, stop],
  );

  // ⚠ **The queue outlives the component.** `speechSynthesis` belongs to the document, not
  // to React: a viewer who leaves the session mid-message would otherwise keep hearing an
  // audit question read out over whatever screen they went to.
  useEffect(
    () => () => {
      generation.current += 1;
      timers.current.forEach((timer) => window.clearTimeout(timer));
      timers.current = [];
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    },
    [],
  );

  return { supported, speaking, problem, toggle, stop };
}
